import { createSlice, PayloadAction, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import {
    AddPatientRequest,
    ChatCompletions,
    DeletePatientRecordsResult,
    Doctor,
    PartialDoctor,
    PatientRecord,
    PatientStage,
    Resource,
    TranscriptSection,
    TranscriptSectionStatus,
} from "@common/utils/types";
import {
    fetchPatientRecords,
    updatePatientRecord as updatePatientRecordSupabase,
    deletePatientRecords,
    fetchPatientTableRecord,
    supabase,
} from "@common/lib/supabaseClient";
import { getWeekRange, isSameDay, isSameWeek, transformTableRecordToPatientRecord } from "@common/utils/helpers";
import { RootState } from "@/app/store";
import { addPatient } from "@/common/lib/api";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";

/*
NOTES:

(1) Redux toolkit allows us to write "mutating" logic in reducers. 
It uses Immer inside to allow us to write simpler immutable updates.
 In all other places, direct object/array mutations are not allowed.
*/

export type SortedColumn = {
    column: "scheduled_at" | "patient_name" | string;
    direction: "asc" | "desc";
};

export type status = "idle" | "loading" | "succeeded" | "failed";

// utility function to filter records by multiple doctors
const isRecordForSelectedDoctors = (record: PatientRecord, selectedDoctors: Doctor[]) => {
    if (selectedDoctors.length === 0) return true; // Show all if no doctors selected
    return selectedDoctors.some(
        (doctor) =>
            record.doctors?.id === doctor.id ||
            record.doctor_id === doctor.id ||
            (record.doctors === null && doctor.id === ""),
    );
};

// utility function to filter records by multiple resources (happy-bridge "resources" calendar_mode).
// Mirrors isRecordForSelectedDoctors: ResourceMultiSelect's synthetic id="" "Unassigned" option,
// when explicitly selected, matches records with no resource_id.
const isRecordForSelectedResources = (record: PatientRecord, selectedResources: Resource[]) => {
    if (selectedResources.length === 0) return true; // Show all if no resources selected
    return selectedResources.some((resource) =>
        resource.id === "" ? !record.resource_id : resource.id === record.resource_id,
    );
};

const initialState: {
    tableRecords: PatientRecord[];
    status: status;
    error: string | null;
    sortedColumn: SortedColumn;
    // Empty array = show all stages (EN-1003 multi-select). Defaults to [Ongoing] to preserve
    // the prior single-select default view.
    currentStages: PatientStage[];
    // EN-1494: ids of the visits ticked in the list, in the order ticked (display order after select-all). Records are looked up in tableRecords.
    selectedTableRecordIds: string[];
    view: "day" | "week";
    date: string; // selected date in ISO format
    // EN-1312: requestId of the most recently issued fetchPatientRecordsAsync. Several can be in
    // flight when the date changes quickly, and only this one may write the table, whatever order
    // they resolve in. clearTableRecords resets it to null, so no fetch issued before a clear can write.
    latestFetchRequestId: string | null;
} = {
    tableRecords: [],
    status: "idle",
    error: null,
    sortedColumn: {
        column: "scheduled_at",
        direction: "asc",
    },
    currentStages: [PatientStage.Ongoing],
    selectedTableRecordIds: [],
    view: "day",
    date: new Date().toISOString(),
    latestFetchRequestId: null,
};

export const patientTableRecordsSlice = createSlice({
    name: "patientTableRecords",
    initialState,
    reducers: {
        addRecord: (state, action: PayloadAction<PatientRecord>) => {
            const record = state.tableRecords.find((record) => record.id === action.payload.id);
            if (!record) {
                state.tableRecords.push(action.payload);
            }
        },
        removeRecord: (state, action: PayloadAction<{ id: string }>) => {
            state.tableRecords = state.tableRecords.filter((record) => record.id !== action.payload.id);
        },
        removeRecords: (state, action: PayloadAction<{ ids: string[] }>) => {
            state.tableRecords = state.tableRecords.filter((record) => !action.payload.ids.includes(record?.id));
        },
        addDocument: (state, action: PayloadAction<{ id: string; chatCompletion: ChatCompletions }>) => {
            const { id, chatCompletion } = action.payload;
            const record = state.tableRecords.find((record) => record.id === id);
            if (record) {
                if (!record?.chat_completions) {
                    record.chat_completions = [chatCompletion];
                } else {
                    record.chat_completions = record.chat_completions.concat(chatCompletion);
                }
            }
        },
        removeDocument: (state, action: PayloadAction<{ id: string | null | undefined; documentId: string }>) => {
            const { id, documentId } = action.payload;
            const record = state.tableRecords.find((record) => record.id === id);
            if (record?.chat_completions) {
                record.chat_completions = record.chat_completions.filter((doc) => doc.id !== documentId);
            }
        },
        incrementRecordingCount: (
            state,
            action: PayloadAction<{
                transcriptionId: string;
                sectionId: string;
                status: TranscriptSectionStatus | null;
            }>,
        ) => {
            const record = state.tableRecords.find(
                (record) => record.transcriptions?.id === action.payload.transcriptionId,
            );
            if (record && record.transcriptions?.transcript_sections) {
                // Only update existing sections — never push. The push-branch was the source
                // of phantom rows in the visit-list table when a section had been discarded
                // but a late realtime transcription_event arrived. If the section isn't in
                // the row's transcript_sections array, leave it alone — the next refetch
                // will reconcile.
                record.transcriptions.transcript_sections = record.transcriptions.transcript_sections.map((section) =>
                    section.id === action.payload.sectionId ? { ...section, status: action.payload.status } : section,
                );
            }
        },
        addRecordingSection: (
            state,
            action: PayloadAction<{
                transcriptionId: string;
                sectionId: string;
                status: TranscriptSectionStatus | null;
            }>,
        ) => {
            // Explicit-add for cross-client new sections (e.g., iOS just created a clip).
            // Mirror of activePatientDataSlice.addTranscriptSection — incrementRecordingCount
            // is update-only (no push, to prevent resurrection), so the visit-list row needs
            // its own explicit-add path or it stays stale until the next refetch. Idempotent:
            // if the section is already present, merge-updates its status.
            //
            // CALLER CONTRACT: this reducer has NO tombstone guard (tombstones live on
            // activePatientDataSlice, cross-slice reads in a reducer would be an anti-pattern).
            // All callers MUST check `activePatientData.discardedSectionIds` before dispatching.
            // Today the only caller is RealtimeProvider.handleTranscriptionEvent, which gates
            // this dispatch behind the same tombstone check that protects addTranscriptSection.
            // If you add a new caller, replicate that check or you reintroduce the EN-610
            // resurrection vector for the visit-list table.
            const record = state.tableRecords.find(
                (record) => record.transcriptions?.id === action.payload.transcriptionId,
            );
            if (!record || !record.transcriptions) return;
            if (!record.transcriptions.transcript_sections) {
                record.transcriptions.transcript_sections = [];
            }
            const existingIndex = record.transcriptions.transcript_sections.findIndex(
                (section) => section.id === action.payload.sectionId,
            );
            if (existingIndex !== -1) {
                record.transcriptions.transcript_sections[existingIndex] = {
                    ...record.transcriptions.transcript_sections[existingIndex],
                    status: action.payload.status,
                };
            } else {
                record.transcriptions.transcript_sections.push({
                    id: action.payload.sectionId,
                    transcription_id: action.payload.transcriptionId,
                    status: action.payload.status,
                } as TranscriptSection);
            }
        },
        decrementRecordingCount: (state, action: PayloadAction<{ transcriptionId: string; sectionIds: string[] }>) => {
            const record = state.tableRecords.find(
                (record) => record.transcriptions?.id === action.payload.transcriptionId,
            );
            if (record && record.transcriptions?.transcript_sections) {
                const sectionIdsSet = new Set(action.payload.sectionIds);
                const filteredSections = record.transcriptions.transcript_sections.filter(
                    (section) => !sectionIdsSet.has(section.id),
                );
                record.transcriptions.transcript_sections = filteredSections;
            }
        },
        updateTableRecordStage: (state, action: PayloadAction<{ id: string; newStage: PatientStage }>) => {
            const record = state.tableRecords.find((record) => record.id === action.payload.id);
            if (record) {
                record.stage = action.payload.newStage;
            }
        },
        //WARNING: This dangerous and can lead to overwriting un nested data like transcriptions or chat completions. Only pass full values here.
        updateTableRecord: (state, action: PayloadAction<{ id: string; updatedRecord: PatientRecord }>) => {
            const recordIndex = state.tableRecords.findIndex((record) => record.id === action.payload.id);
            if (recordIndex !== -1) {
                state.tableRecords[recordIndex] = {
                    ...state.tableRecords[recordIndex],
                    ...action.payload.updatedRecord,
                };
            }
        },
        //WARNING: This dangerous and can lead to overwriting un nested data like transcriptions or chat completions. Only pass full values here.
        addOrUpdateRecord: (state, action: PayloadAction<PatientRecord>) => {
            const patientRecord = action.payload;
            const record = state.tableRecords.findIndex((record) => record.id === patientRecord.id);
            const { view, date } = state;

            // get the selected date
            const selectedDate = new Date(date);

            // check if the record is scheduled for today or this week depending on view
            const isScheduledToday =
                view === "day"
                    ? isSameDay(new Date(patientRecord?.scheduled_at || ""), selectedDate)
                    : isSameWeek(new Date(patientRecord?.scheduled_at || ""), selectedDate);

            // If the record already exists, update it
            if (record !== -1) {
                state.tableRecords[record] = { ...state.tableRecords[record], ...patientRecord };
                // If the record doesn't exist, add it
            } else if (isScheduledToday) {
                state.tableRecords.push(patientRecord);
            }
        },
        addPatientRecord: (state, action: PayloadAction<PatientRecord>) => {
            // check if the record already exists
            const record = state.tableRecords.find((record) => record.id === action.payload.id);

            // if the record doesn't exist, and it's scheduled for today and belongs to the correct doctor add it where it belongs in the table based of scheduled_at ASC.
            if (!record) {
                state.tableRecords.push(action.payload);
            }
        },
        updateTranscriptSectionStatus: (
            state,
            action: PayloadAction<{ id: string; sectionId: string; status: TranscriptSectionStatus }>,
        ) => {
            const record = state.tableRecords.find((record) => record.id === action.payload.id);
            if (record && record.transcriptions?.transcript_sections) {
                const updatedTranscriptSections = record.transcriptions.transcript_sections.map((section) => {
                    if (section.id === action.payload.sectionId) {
                        return { ...section, status: action.payload.status };
                    }
                    return section;
                });
                record.transcriptions.transcript_sections = updatedTranscriptSections;
            }
        },
        setSortedColumn: (state, action: PayloadAction<SortedColumn>) => {
            state.sortedColumn = action.payload;
        },
        setCurrentStages: (state, action: PayloadAction<PatientStage[]>) => {
            state.currentStages = action.payload;
        },
        toggleSelectedTableRecordId: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            state.selectedTableRecordIds = state.selectedTableRecordIds.includes(id)
                ? state.selectedTableRecordIds.filter((existing) => existing !== id)
                : [...state.selectedTableRecordIds, id];
        },
        setSelectedTableRecordIds: (state, action: PayloadAction<string[]>) => {
            state.selectedTableRecordIds = Array.from(new Set(action.payload));
        },
        resetSelectedTableRecordIds: (state) => {
            state.selectedTableRecordIds = [];
        },
        // Drops just these ids, so visits ticked while a bulk request was in flight stay selected.
        deselectTableRecordIds: (state, action: PayloadAction<string[]>) => {
            const drop = new Set(action.payload);
            state.selectedTableRecordIds = state.selectedTableRecordIds.filter((id) => !drop.has(id));
        },
        updateTableRecordsDoctor: (state, action: PayloadAction<{ ids: string[]; doctor: PartialDoctor }>) => {
            const { ids, doctor } = action.payload;
            const idSet = new Set(ids);
            state.tableRecords.forEach((record) => {
                if (record.id && idSet.has(record.id)) {
                    record.doctor_id = doctor.id;
                    record.doctors = doctor;
                }
            });
        },
        setStatus: (state, action: PayloadAction<status>) => {
            state.status = action.payload;
        },
        setView: (state, action: PayloadAction<"day" | "week">) => {
            state.view = action.payload;
        },
        setDate: (state, action: PayloadAction<string>) => {
            state.date = action.payload;
        },
        clearTableRecords: (state) => {
            state.tableRecords = [];
            state.selectedTableRecordIds = [];
            state.status = "idle";
            state.error = null;
            state.latestFetchRequestId = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPatientRecordsAsync.pending, (state, action) => {
                state.status = "loading";
                state.latestFetchRequestId = action.meta.requestId;
            })
            .addCase(fetchPatientRecordsAsync.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.latestFetchRequestId) return;
                state.status = "succeeded";
                state.tableRecords = action.payload as PatientRecord[];
            })
            .addCase(fetchPatientRecordsAsync.rejected, (state, action) => {
                if (action.meta.requestId !== state.latestFetchRequestId) return;
                state.status = "failed";
                state.error = action.error?.message || null;
            });
    },
});

//Export Async Thunks
interface FetchPatientRecordsArgs {
    selectedDate: any;
    doctorIds: string[] | null;
    abortSignal: AbortSignal;
    userId?: string;
}

export const fetchPatientRecordsAsync = createAsyncThunk(
    "patientRecords/fetchPatientRecordsAsync",
    async ({ selectedDate, doctorIds, abortSignal, userId }: FetchPatientRecordsArgs, { getState }) => {
        const state = getState() as RootState;
        const { view } = state.patientTableRecords;

        // Do not attempt to fetch until authenticated (no session and no explicit userId). Skip
        // this check entirely when the caller already resolved a userId (every current call site
        // does): getUser() revalidates the JWT against the Auth server, an extra network round
        // trip that only matters as a guard for a caller with no userId to fall back on.
        if (!userId) {
            try {
                const {
                    data: { user },
                } = await supabase.auth.getUser();
                if (!user) {
                    return [] as PatientRecord[];
                }
            } catch {
                // If auth check fails, avoid fetching
                return [] as PatientRecord[];
            }
        }

        let patientRecords: PatientRecord[] = [];

        if (view === "day") {
            // fetch all appointments for the day
            const response = await fetchPatientRecords(selectedDate, doctorIds, abortSignal, userId);
            // fetchPatientRecords resolves (never rejects) even on a genuine failure -- session
            // expiry, a 500, rate limiting -- returning { data: [], error } instead (PR review,
            // 2026-08-26). createAsyncThunk only dispatches `rejected` on an actual throw, so
            // without this check every one of those failures looked identical to a legitimate
            // "no records for this range" success, silently defeating both the stale-data warning
            // below (recordStatus never became "failed") and any other rejected-based handling.
            // An aborted fetch (fetchSequentially re-running on a session, impersonation or clinic
            // change) is not a failure. An abort during the patient_record query resolves with error: null
            // (retryWrapper), so it skips this check; an abort during the account lookup throws
            // AbortError and dispatches rejected. Either way a superseded fetch cannot write state:
            // the reducers ignore any requestId other than latestFetchRequestId (EN-1312).
            if (response.error) {
                throw response.error;
            }
            patientRecords = response.data;
        } else {
            // fetch all appointments for the week
            const weekRange = getWeekRange(selectedDate);

            const response = await fetchPatientRecords(weekRange, doctorIds, abortSignal, userId);
            if (response.error) {
                throw response.error;
            }
            patientRecords = response.data;
        }

        return patientRecords;
    },
);

interface InsertPatientRecordArgs {
    newPatientRecord: AddPatientRequest;
    isTodayOrWeek: boolean;
}

export const insertPatientRecord = createAsyncThunk(
    "patientRecords/insertPatientRecord",
    async ({ newPatientRecord, isTodayOrWeek = true }: InsertPatientRecordArgs, { dispatch }) => {
        const { data, error } = await addPatient(newPatientRecord);

        if (error) {
            toast.error("Failed to insert patient. Please try again.");
            Sentry.captureException(error);
            return null;
        }

        // The API response can omit appointment_duration / scheduled_at on the optimistic
        // echo; the request itself is the source of truth for these (EN-624). Without this
        // merge the calendar maps the row to a zero-length event, which then balloons to
        // hours when dragged because the next-day clamp interacts with the degenerate end.
        const optimisticRow = data
            ? {
                  ...data,
                  appointment_duration: data.appointment_duration ?? newPatientRecord.appointmentDuration ?? 30,
                  scheduled_at: data.scheduled_at ?? newPatientRecord.scheduledAt,
              }
            : null;

        if (isTodayOrWeek && optimisticRow) {
            dispatch(addRecord(optimisticRow));
        }

        return optimisticRow;
    },
);
interface UpdatePatientRecordArgs {
    updatedTableRecord: PatientRecord;
    updateTable: boolean;
}

export const updatePatientRecord = createAsyncThunk(
    "patientRecords/updatePatientRecord",
    async ({ updatedTableRecord, updateTable }: UpdatePatientRecordArgs, thunkAPI) => {
        const updated = transformTableRecordToPatientRecord(updatedTableRecord);
        // updatePatientRecordSupabase now throws directly on a genuine Supabase/PostgREST
        // error (distinguished there from a benign aborted/cancelled request, which it
        // echoes back as a non-null no-op instead) — so `pr` should never actually be
        // falsy here. Kept as a defensive backstop: without it, every caller downstream
        // (including two call sites that already `.unwrap()` this thunk expecting it to
        // reject: TimeGrid.tsx, VirtualizedResourceCalendar.tsx) would silently treat a
        // failed persist as a success, dispatching the optimistic local update anyway.
        const pr = await updatePatientRecordSupabase(updated);
        if (!pr) {
            throw new Error(`Failed to update patient record ${updatedTableRecord.id}`);
        }
        if (updateTable) {
            // update the record in the table
            thunkAPI.dispatch(updateTableRecord({ id: updatedTableRecord.id, updatedRecord: updatedTableRecord }));
        } else {
            //Remove it, update value different from filters
            thunkAPI.dispatch(removeRecord({ id: updatedTableRecord.id }));
        }

        return pr;
    },
);

export const deletePatientRecordsAsync = createAsyncThunk(
    "patientRecords/deletePatientRecordsAsync",
    async (
        { ids, force, expectedAccountId }: { ids: string[]; force?: boolean; expectedAccountId?: string },
        thunkAPI,
    ): Promise<DeletePatientRecordsResult> => {
        const options = { ...(force ? { force } : {}), ...(expectedAccountId ? { expectedAccountId } : {}) };
        const result = await deletePatientRecords(ids, Object.keys(options).length > 0 ? options : undefined);

        // EN-1088: only remove ids that deletePatientRecords actually archived. This used to
        // dispatch removeRecords for every requested id BEFORE the call and discard the result,
        // so a refused delete (a live recording lease - see "The mechanism, verified" in the
        // EN-1088 plan) removed the visit from the redux store while it survived untouched in
        // the database. The guard worked and the UI lied in the opposite direction. Callers also
        // need the typed result to surface a partial outcome, so it's returned here too.
        // EN-1088: notFoundIds are removed too, for the opposite reason - the row is not there
        // for this user at all (deleted from another device, or a stale id from a list left
        // open), so the end state the vet asked for is already true. Leaving it in the store
        // would be its own silent failure: click delete, see no error, row stays.
        const idsToRemove = [...result.deletedIds, ...result.notFoundIds];

        if (idsToRemove.length > 0) {
            thunkAPI.dispatch(removeRecords({ ids: idsToRemove }));
        }

        return result;
    },
);

// This thunk is used to handle the patient event broadcasted from realtime
export const handlePatientTableBroadcast = createAsyncThunk(
    "patientRecords/handlePatientTableBroadcast",
    async ({ patientRecordId }: { patientRecordId: string }, { dispatch, getState }) => {
        const state = getState() as RootState;
        const { selectedDoctors } = state.doctors;
        const { tableRecords, view, date } = state.patientTableRecords;

        // get the selected date
        const selectedDate = new Date(date);

        // EN-1325: the board-row select, not the full patient-view embed (fetchPatientRecord).
        const { data: patientRecord, error } = await fetchPatientTableRecord(patientRecordId);

        if (error || !patientRecord) {
            return null;
        }

        // check if the record already exists
        const tableRecord = tableRecords.find((record) => record.id === patientRecord.id);

        // check if the record is scheduled for today or this week depending on view
        const isScheduledToday =
            view === "day"
                ? isSameDay(new Date(patientRecord?.scheduled_at || ""), selectedDate)
                : isSameWeek(new Date(patientRecord?.scheduled_at || ""), selectedDate);

        // check if the record belongs to the correct doctor or if no doctor is selected
        const isCorrectDoctor = isRecordForSelectedDoctors(patientRecord, selectedDoctors);

        // if patient event doesn't exist, add it to the table
        if (!tableRecord && isScheduledToday && isCorrectDoctor) {
            dispatch(addRecord(patientRecord));
        } else if (tableRecord) {
            // if the record exists in the table, update it
            dispatch(updateTableRecord({ id: patientRecord.id, updatedRecord: patientRecord }));
        }

        return patientRecord;
    },
);

//Export the actions (add these here to call them into thunks)
export const {
    addRecord,
    removeRecord,
    addOrUpdateRecord,
    removeRecords,
    addDocument,
    updateTableRecord,
    setSortedColumn,
    setCurrentStages,
    updateTableRecordStage,
    incrementRecordingCount,
    addRecordingSection,
    toggleSelectedTableRecordId,
    setSelectedTableRecordIds,
    resetSelectedTableRecordIds,
    deselectTableRecordIds,
    updateTableRecordsDoctor,
    decrementRecordingCount,
    setStatus,
    removeDocument,
    updateTranscriptSectionStatus,
    setView,
    setDate,
    clearTableRecords,
} = patientTableRecordsSlice.actions;

//Export Selectors
export const selectAllTableRecords = (state: { patientTableRecords: { tableRecords: PatientRecord[] } }) =>
    state.patientTableRecords.tableRecords;

export const selectTableRecordStatus = (state: { patientTableRecords: { status: status } }) =>
    state.patientTableRecords.status;

export const selectSelectedTableRecordIds = (state: RootState): string[] =>
    state.patientTableRecords.selectedTableRecordIds;

export const selectSortedColumn = (state: { patientTableRecords: { sortedColumn: SortedColumn } }) =>
    state.patientTableRecords.sortedColumn;

export const selectCurrentStages = (state: { patientTableRecords: { currentStages: PatientStage[] } }) =>
    state.patientTableRecords.currentStages;

export const selectPatientRecordById = (state: RootState, patientRecordId: string) =>
    state.patientTableRecords.tableRecords.find((record) => record.id === patientRecordId);

export const doesPatientRecordExist = (state: RootState, patientRecordId: string) =>
    state.patientTableRecords.tableRecords.some((record) => record.id === patientRecordId);

export const selectSortedTableRecordsByStage = createSelector(
    [
        selectAllTableRecords,
        (state: RootState) => state.patientTableRecords.sortedColumn,
        (state: RootState) => state.patientTableRecords.currentStages,
        (state: RootState) => state.doctors.selectedDoctors,
        (state: RootState) => state.resources.selectedResources,
        (_state: RootState, calendarMode: "doctors" | "resources" = "doctors") => calendarMode,
    ],
    (tableRecords, sortedColumn, currentStages, selectedDoctors, selectedResources, calendarMode) => {
        return tableRecords
            .filter((record) => {
                // Empty selection = show all stages (EN-1003 multi-select).
                if (currentStages.length > 0 && !currentStages.includes(record.stage as PatientStage)) return false;
                return calendarMode === "resources"
                    ? isRecordForSelectedResources(record, selectedResources)
                    : isRecordForSelectedDoctors(record, selectedDoctors);
            })
            .sort((a, b) => {
                let valA = a[sortedColumn.column as keyof PatientRecord];
                let valB = b[sortedColumn.column as keyof PatientRecord];

                if (sortedColumn.column === "patient_name") {
                    // Convert patient names to lowercase for case-insensitive sorting
                    valA = typeof valA === "string" ? valA.toLowerCase() : "";
                    valB = typeof valB === "string" ? valB.toLowerCase() : "";
                }
                if (!valA || !valB) return 0;
                return (valA < valB ? -1 : 1) * (sortedColumn.direction === "asc" ? 1 : -1);
            });
    },
);

export const selectTableRecordById = createSelector(
    [selectAllTableRecords, (state, id: string) => id],
    (tableRecords, id) => {
        const record = tableRecords.find((record) => record.id === id);
        return record ? record : null;
    },
);

const isRecordForSelectedFilter = (
    record: PatientRecord,
    calendarMode: "doctors" | "resources",
    selectedDoctors: Doctor[],
    selectedResources: Resource[],
) =>
    calendarMode === "resources"
        ? isRecordForSelectedResources(record, selectedResources)
        : isRecordForSelectedDoctors(record, selectedDoctors);

export const selectOngoingRecordCount = createSelector(
    [
        selectAllTableRecords,
        (state: RootState) => state.doctors.selectedDoctors,
        (state: RootState) => state.resources.selectedResources,
        (_state: RootState, calendarMode: "doctors" | "resources" = "doctors") => calendarMode,
    ],
    (tableRecords, selectedDoctors, selectedResources, calendarMode) => {
        return tableRecords.filter(
            (record) =>
                record.stage === PatientStage.Ongoing &&
                isRecordForSelectedFilter(record, calendarMode, selectedDoctors, selectedResources),
        ).length;
    },
);

export const selectReviewRecordCount = createSelector(
    [
        selectAllTableRecords,
        (state: RootState) => state.doctors.selectedDoctors,
        (state: RootState) => state.resources.selectedResources,
        (_state: RootState, calendarMode: "doctors" | "resources" = "doctors") => calendarMode,
    ],
    (tableRecords, selectedDoctors, selectedResources, calendarMode) => {
        return tableRecords.filter(
            (record) =>
                record.stage === PatientStage.Review &&
                isRecordForSelectedFilter(record, calendarMode, selectedDoctors, selectedResources),
        ).length;
    },
);

export const selectFinalizedRecordCount = createSelector(
    [
        selectAllTableRecords,
        (state: RootState) => state.doctors.selectedDoctors,
        (state: RootState) => state.resources.selectedResources,
        (_state: RootState, calendarMode: "doctors" | "resources" = "doctors") => calendarMode,
    ],
    (tableRecords, selectedDoctors, selectedResources, calendarMode) => {
        return tableRecords.filter(
            (record) =>
                record.stage === PatientStage.Finalized &&
                isRecordForSelectedFilter(record, calendarMode, selectedDoctors, selectedResources),
        ).length;
    },
);

//Export Reducer
export default patientTableRecordsSlice.reducer;
